/* eslint-disable no-restricted-imports */
import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  LocalOffer as LocalOfferIcon,
  MonetizationOn as MonetizationOnIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@material-ui/icons';

import PropTypes from 'prop-types';
import { xpcellService } from 'services';

import { useStyles } from './styles';

export default function CreateProductDialog({ open, onClose, onProductCreated }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    part_number: '',
    description: '',
    sell_price: '',
    cost_price: '',
    available_quantity: '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.part_number) {
      toast.error('Part Number é obrigatório');
      return;
    }

    if (!formData.sell_price || parseFloat(formData.sell_price) <= 0) {
      toast.error('Valor de venda deve ser maior que zero');
      return;
    }

    if (!formData.cost_price || parseFloat(formData.cost_price) <= 0) {
      toast.error('Valor de custo deve ser maior que zero');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        part_number: formData.part_number.toUpperCase(),
        sell_price: parseFloat(formData.sell_price),
        cost_price: parseFloat(formData.cost_price),
        process: [1],
        active: 1,
      };

      await xpcellService.createComponentSellPrice(payload);
      toast.success('Produto cadastrado com sucesso!');

      const newProduct = {
        part_number: formData.part_number.toUpperCase(),
        description: formData.description || formData.part_number,
        sell_price: parseFloat(formData.sell_price),
        cost_price: parseFloat(formData.cost_price),
      };

      if (onProductCreated) {
        onProductCreated(newProduct);
      }

      setFormData({
        part_number: '',
        description: '',
        sell_price: '',
        cost_price: '',
      });

      onClose();
    } catch (error) {
      console.error('Erro ao criar produto:', error);
      toast.error(error.response?.data?.message || 'Erro ao cadastrar produto. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center'>
            <ShoppingCartIcon className={classes.titleIcon} />
            <Typography variant='h6'>Cadastrar Novo Produto</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Part Number'
              value={formData.part_number}
              onChange={(e) => handleChange('part_number', e.target.value.toUpperCase())}
              variant='outlined'
              required
              placeholder='Ex: GH82-12345A'
              helperText='Código único do produto (obrigatório)'
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Descrição'
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              variant='outlined'
              placeholder='Ex: Display LCD Samsung A50'
              helperText='Descrição do produto (opcional)'
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Valor de Custo'
              type='number'
              value={formData.cost_price}
              onChange={(e) => handleChange('cost_price', e.target.value)}
              variant='outlined'
              required
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{
                startAdornment: <LocalOfferIcon className={classes.inputIcon} color='action' />,
              }}
              helperText='Preço de custo do produto'
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Valor de Venda'
              type='number'
              value={formData.sell_price}
              onChange={(e) => handleChange('sell_price', e.target.value)}
              variant='outlined'
              required
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{
                startAdornment: <MonetizationOnIcon className={classes.inputIcon} color='action' />,
              }}
              helperText='Preço de venda do produto'
            />
          </Grid>

          {formData.cost_price && formData.sell_price && (
            <Grid item xs={12}>
              <Box className={classes.marginInfo}>
                <Typography variant='body2' color='textSecondary'>
                  <strong>Margem de Lucro:</strong>{' '}
                  {(
                    ((parseFloat(formData.sell_price) - parseFloat(formData.cost_price)) /
                      parseFloat(formData.cost_price)) *
                    100
                  ).toFixed(2)}
                  %
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} disabled={loading} color='default'>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant='contained'
          color='primary'
          className={classes.submitButton}
        >
          {loading ? 'Cadastrando...' : 'Cadastrar Produto'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CreateProductDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProductCreated: PropTypes.func,
};
